import { ref } from 'vue';
import { isChubbLegacyEnabled } from '@/config/chubbLegacy';
import {
  fetchLegacyChubbQuote,
  hasAuthenticDemographics,
  isChubbPlanPaymentAllowed,
} from '@/services/chubbLegacyQuote';
import type { ChubbProviderSession, ChubbQuotePayload } from '@/services/chubbApi';
import type { CotizaTodasResponse } from '@/types/quote.types';

const toChubbErrorMessage = (err: unknown): string => {
  const message = err instanceof Error ? err.message : 'Error al cotizar con Chubb';
  return !message || message.includes('Chubb') ? message : `Chubb: ${message}`;
};

const canQuoteChubb = (
  payload: ChubbQuotePayload | null,
  formaPago: string,
  paquete: string,
): payload is ChubbQuotePayload =>
  isChubbLegacyEnabled() &&
  isChubbPlanPaymentAllowed(formaPago, paquete) &&
  !!payload &&
  hasAuthenticDemographics(payload.gender, payload.birthDate);

/** Chubb state for the legacy comparator, isolated from Qualitas/ANA state. */
export const useChubbQuote = () => {
  const chubbQuotes = ref<CotizaTodasResponse | null>(null);
  const chubbError = ref<string | null>(null);
  const chubbProviderSession = ref<ChubbProviderSession | null>(null);

  const clearChubbQuote = (): void => {
    chubbQuotes.value = null;
    chubbError.value = null;
    chubbProviderSession.value = null;
  };

  /**
   * Parallel Chubb fetch for legacy comparator. Never throws to callers —
   * failures stay in chubbError so Qualitas/ANA are untouched.
   */
  const fetchChubbQuote = async (
    payload: ChubbQuotePayload | null,
    formaPago: string,
    paquete: string,
  ): Promise<void> => {
    clearChubbQuote();
    if (!canQuoteChubb(payload, formaPago, paquete)) return;

    try {
      const result = await fetchLegacyChubbQuote(payload);
      chubbQuotes.value = result.mapped;
      chubbProviderSession.value = result.session;
    } catch (err) {
      chubbError.value = toChubbErrorMessage(err);
    }
  };

  return { chubbQuotes, chubbError, chubbProviderSession, clearChubbQuote, fetchChubbQuote };
};
